import { Card, Result, Tag } from 'antd';
import { decodeEntities, getGroovyFuncs, getStructureDefProfileUrl } from '../../utils/util';
import React, { useEffect, useState } from 'react';

/**
 * Forms generic string template with pre-defined parameters
 * that can be passed in later
 */
const template = (strings, ...keys) => {
  return (...values) => {
    const dict = values[values.length - 1] || {};
    const result = [strings[0]];
    keys.forEach((key, i) => {
      const value = Number.isInteger(key) ? values[key] : dict[key];
      result.push(value, strings[i + 1]);
    });
    return result.join('');
  };
};

const warningsTemplate = template`{
  "resourceType":"OperationOutcome",
  "id":"Error",
  "text":{
    "status":"additional",
    "div":"<div xmlns='http://www.w3.org/1999/xhtml'><p>ERROR</p></div>"
  },
  "issue": [${'issueList'}]
}`;

const issuesTemplate = template`{
  "severity":"${'issueSeverity'}",
  "code":"invariant",
  "details":{
    "text": "${'issueText'}",
    "allowedVals": "${'allowedVals'}",
    "minMax": "${'minMax'}"
  },
  "expression":[
    "${'issueExpression'}"
  ]
}`;

const MappingReviewWarnings = props => {
  const { dataSource, IG } = props;
  const [issues, setIssues] = useState([]);
  const [warnings, setWarnings] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const msg = hasErrors
    ? 'Mapping is not conformant to the FHIR resource structure definition and/or profile. Please address the errors listed below before requesting your review.'
    : 'Mapping cannot be validated to FHIR Structure definition. There may still be issues if fields present in source data are omitted from output FHIR resource.';
  const stat = hasErrors ? 'error' : 'warning';
  const header = hasErrors ? 'Error' : 'Warning';

  const getTagColor = issue => {
    let color = 'green';

    switch (issue.severity) {
      case 'fatal':
        color = 'red';
        break;
      case 'error':
        color = 'magenta';
        break;
      case 'warning':
        color = 'orange';
        break;
      case 'info':
        color = 'lime';
        break;
      default:
    }

    return color;
  };

  /**
   * Adds issue to issues list
   */
  const addIssue = issue => {
    if (!issues.includes(issue)) {
      const missingIssues = issues;
      missingIssues.push(issue);
      setIssues(missingIssues);
    }
  };

  /**
   * Utilizes issuesTemplate to create new issue
   */
  const makeIssue = (error, text, allowed, severity) => {
    return issuesTemplate({
      issueText: text,
      allowedVals: `Allowed Values: ${allowed}`,
      issueExpression: `${error.structurDef.path}`,
      minMax: `Minimum needed: ${error.structurDef.min}, Maximum: ${error.structurDef.max}`,
      issueSeverity: severity,
    });
  };

  /**
   * Ensures issues list contains at least one error
   * used to set warnings header
   */
  const checkErrors = () => {
    for (const i in issues) {
      if (JSON.parse(issues[i]).severity.includes('error')) {
        setHasErrors(true);
        break;
      }
    }
  };

  /**
   * Adds potential issues to errorsToCheck list
   * @param {*} errorsToCheck
   * @param {*} profile
   * @param {*} i
   * @param {*} row
   */
  const addErrorsToCheck = (errorsToCheck, profile, i, row = null) => {
    errorsToCheck.push({
      structurDef: {
        path: profile.paths[i].path,
        allowedVals: profile.paths[i].allowed_values,
        min: profile.min,
        max: profile.max,
      },
      mapping: {
        path: row ? dataSource[row].path : null,
        value: row ? dataSource[row].value.replace('https', 'http') : null,
      },
    });
  };

  /**
   * Runs custom logic to identify errors/warnings to potentially report
   * Returns bool reflecting whether required path found in mappings.
   * @param {*} errorsToCheck
   * @param {*} profile
   * @param {*} i
   */
  const comparePathToMapping = (errorsToCheck, profile, i) => {
    let matchFound = false;
    const rootElement = profile.root_element.split('[')[0];

    for (const row in dataSource) {
      // Verify if mapping path matches structuredef path and structuredef has allowed values
      if (
        dataSource[row].path.toLowerCase() === profile.paths[i].path.toLowerCase() &&
        profile.paths[i].allowed_values !== null
      ) {
        matchFound = true;
        addErrorsToCheck(errorsToCheck, profile, i, row);
      } else if (profile.paths[i].path.includes(']')) {
        /*
         * Else, handle dynamic structuredef path
         * i.e. identifier[memberid].type.coding[0].code
         */
        const ind = profile.paths[i].path.indexOf(']');
        const endOfPath = profile.paths[i].path.slice(ind);

        if (
          dataSource[row].path.startsWith(rootElement) &&
          dataSource[row].path.includes(endOfPath) &&
          profile.paths[i].allowed_values !== null
        ) {
          matchFound = true;
          addErrorsToCheck(errorsToCheck, profile, i, row);
        } else if (dataSource[row].path.startsWith(rootElement)) {
          matchFound = true;
        }
      } else if (dataSource[row].path.startsWith(rootElement)) {
        /*
         * Else, we verify that the mapping path startswith the root element
         * this is to prevent over reporting of errors, as per Avi,
         * more often than not just the root path is required
         */
        matchFound = true;
      }
    }

    return matchFound;
  };

  /**
   * Identifies potential mapping errors to validate against structuredef
   * @param {*} profile
   * @param {*} errorsToCheck
   */
  const findErrorsToCheck = (profile, errorsToCheck) => {
    for (let i = 0; i < profile.paths.length; i += 1) {
      const matchFound = comparePathToMapping(errorsToCheck, profile, i);

      if (!matchFound) {
        addErrorsToCheck(errorsToCheck, profile, i);
      }
    }
  };

  /**
   * Identifies if mapping value contains groovyFunction
   * If mappings contains groovy it will not be compared against
   * allowed values to raise errors.
   * @param {*} gf
   * @param {*} value
   */
  const isGroovyFunc = (gf, value) => {
    for (const func of gf) {
      if (func.value.includes('static def')) {
        const funcName = func.value.split('static def')[1].split('(')[0].trim();

        if (value.includes(funcName)) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Identifies if mapping value contains allowed values
   * as defined by the structuredef table
   * @param {*} error
   */
  const hasAllowedVal = error => {
    let hasAllowed = false;

    for (const val of error.structurDef.allowedVals) {
      if (error.mapping.value.includes(val)) {
        hasAllowed = true;
        break;
      }
    }

    return hasAllowed;
  };

  /**
   * Handles coding errors as warnings, this is currently
   * necessary since the structuredef table does not include
   * whether codes are extensible or not.
   * @param {*} error
   * @param {*} allowed
   */
  const handleCodingErrors = (error, allowed) => {
    const SYSTEM = error.mapping.path.replace('.code', '.system');
    const sysMapping = dataSource.find(mapping => mapping.path === SYSTEM);
    let severity = 'error';
    let text = `Value, ${error.mapping.value.replace(/"/g, '')}, not allowed for path, ${
      error.mapping.path
    }`;

    if (error.mapping.path.endsWith('code') && sysMapping) {
      text += `, please ensure that either the mapping is extensible or verify whether the code is valid : ${sysMapping.value.replace(
        /"/g,
        '',
      )}`;
      severity = 'warning';
    }

    const issue = makeIssue(error, text, allowed, severity);
    addIssue(issue);
  };

  /**
   * Takes errors to check and validates them against
   * their allowed values in the structuredef table
   * @param {*} errors
   */
  const verifyErrors = async errors => {
    const gf = await getGroovyFuncs({ client_id: dataSource[0].client_id });
    errors.forEach(error => {
      let issue = null;
      let text = null;
      const allowed =
        error.structurDef.allowedVals === null
          ? 'any value'
          : error.structurDef.allowedVals.join(', ');

      if (!error.mapping.path) {
        text = `Missing required path: ${error.structurDef.path}`;
        issue = makeIssue(error, text, allowed, 'error');
        addIssue(issue);
      } else if (error.structurDef.allowedVals !== null && !isGroovyFunc(gf, error.mapping.value)) {
        if (error.mapping.value.includes('_.')) {
          text = `Ensure that input value, ${error.mapping.value.replace(
            /"/g,
            '',
          )}, resolves to an allowed value for path,  ${error.mapping.path}`;
          issue = makeIssue(error, text, allowed, 'warning');
          addIssue(issue);
        } else if (!hasAllowedVal(error)) {
          handleCodingErrors(error, allowed);
        }
      }
    });
  };

  /**
   * Gets structuredef based on IG profile (meta.profile[0])
   * calls functions to identify and validate any potential
   * mapping errors
   * @param {*} profileUrl
   */
  const getWarnings = async profileUrl => {
    /* 
      Would need to check what fields in the StructureDefinition table have a min value > 0. 
    */
    const profiles = await getStructureDefProfileUrl(profileUrl);
    const errorsToCheck = [];
    const mustSupport = profiles.elements.filter(element => element.min !== '0');

    if (mustSupport.length === 0) {
      const issue = issuesTemplate({
        issueText: `No Structure Definition found and no checks could be performed for mappings meta.profile[0] field: ${profileUrl}`,
        issueExpression: 'meta.profile[0]',
        issueSeverity: 'warning',
      });
      addIssue(issue);
    } else {
      for (const field of mustSupport) {
        findErrorsToCheck(field, errorsToCheck);
      }

      await verifyErrors(errorsToCheck);
    }

    checkErrors();
  };

  useEffect(() => {
    if (IG !== null) {
      (async () => {
        const profileUrl = IG.replace(/"/g, '').split('|')[0];
        await getWarnings(profileUrl);
      })();
    } else {
      const issue = issuesTemplate({
        issueText: 'Missing required mappings field meta.profile[0]',
        minMax: 'Minimum needed: 1, Maximum: 1',
        issueExpression: 'meta.profile[0]',
        issueSeverity: 'error',
      });
      setHasErrors(true);
      addIssue(issue);
    }
  }, [IG, issues, warnings]);

  useEffect(() => {
    if (issues.length > 0) {
      setWarnings(
        warningsTemplate({
          issueList: issues.toString().replace(/(\r\n|\n|\r)/gm, ''),
        }),
      );
    }
  }, [issues, warnings, hasErrors]);

  return (
    <div>
      {warnings.length > 0 && (
        <>
          <Result status={stat} title={header} subTitle={msg} />
          {JSON.parse(warnings).issue.map(issue => {
            const expressions = issue.expression ? issue.expression : [];
            return (
              <Card key={issue.details.text}>
                <Tag color={getTagColor(issue)}>{issue.severity}</Tag>
                <Tag style={{ marginBottom: 5 }} color={getTagColor(issue)}>
                  {issue.code}
                </Tag>
                {expressions.map(expression => (
                  <Tag key={expression} color={getTagColor(issue)}>
                    {expression}
                  </Tag>
                ))}

                <p style={{ marginBottom: 0, marginTop: 0 }}>
                  {decodeEntities(issue.details.text)}
                </p>
                <p style={{ marginBottom: 0, marginTop: 0 }}>
                  {decodeEntities(issue.details.minMax)}
                </p>
                {issue.details.allowedVals && <p>{decodeEntities(issue.details.allowedVals)}</p>}
              </Card>
            );
          })}
          <br />
        </>
      )}
    </div>
  );
};

export default MappingReviewWarnings;
